import { fetchProjectChat, getSDK } from '@littlebird/sdk';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { logError } from '@/lib/errorLogging';

import { firstNameOf } from './team-chat-messages';
import { teamChatsEnabled } from './use-team-chat-config';

const POST_MODALITY = '"type":"team_chat_post"';

const joinNames = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

/** Before a chat that teammates posted in is deleted for everyone, says who posted and how much goes with it */
export async function confirmTeamChatDeletion(
  chatIds: readonly string[]
): Promise<boolean> {
  if (!teamChatsEnabled()) return true;
  const sdk = getSDK();
  for (const chatId of chatIds) {
    const thread = sdk.stores.threads.getById(chatId);
    if (!thread?.projectId) continue;
    let detail;
    try {
      detail = await fetchProjectChat(sdk, thread.projectId, chatId);
    } catch (error) {
      logError(error, 'Team chat deletion check failed');
      continue;
    }
    const posters = detail.posters ?? [];
    if (posters.length === 0) continue;
    const postCount = detail.messages.filter((message) =>
      message.some(
        (part) =>
          part.startsWith('modalities:') &&
          part.replace(/\s/g, '').includes(POST_MODALITY)
      )
    ).length;
    const projectName =
      sdk.stores.projects.getById(thread.projectId)?.name ?? 'the project';
    const names = joinNames(posters.map((poster) => firstNameOf(poster.name)));
    const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
      title: 'Delete this team chat?',
      description: `${names} posted here. Deleting removes the chat and their ${postCount} ${postCount === 1 ? 'post' : 'posts'} for everyone in ${projectName}. This cannot be undone.`,
      confirmText: 'Delete for everyone',
      cancelText: 'Cancel',
    });
    if (!confirmed) return false;
  }
  return true;
}
